import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { isBillingForbidden } from "@/api/billing";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { appConfig } from "@/config/appConfig";
import { useBillingPurchaseReturn } from "@/hooks/useBillingPurchaseReturn";
import { queryKeys } from "@/lib/query-keys";
import {
  type BillingPurchaseSnapshot,
  reconcileAttempt,
  requiresPurchaseReview,
} from "./billing-attempt";
import { formatMinor } from "./billing-format";

function statusLabel(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1).replaceAll("_", " ");
}

/** Shows authoritative payment and fulfillment inline or in a dialog; closing it preserves the polling deadline. */
export function PurchaseStatus({
  orgId,
  purchaseId,
  onStatus,
  dialog,
}: {
  orgId: string;
  purchaseId: string;
  onStatus: (data: BillingPurchaseSnapshot) => void;
  dialog?: {
    open: boolean;
    onClose: () => void;
    onViewInvoices: () => void;
    restoreFocus: () => void;
  };
}) {
  const status = useBillingPurchaseReturn(orgId, purchaseId);
  const client = useQueryClient();
  const data = status.data;
  const reviewRequired = data ? requiresPurchaseReview(data) : false;
  useEffect(() => {
    // Replay owns reconciliation until its response is bound, even across org view remounts.
    if (!data || status.checkoutPending || isBillingForbidden(status.error))
      return;
    try {
      reconcileAttempt(orgId, data);
    } catch {
      // Storage may be disabled after Checkout; authoritative status remains usable.
    }
    onStatus(data);
    if (data.fulfillment_status === "fulfilled") {
      for (const queryKey of [
        queryKeys.billing.summary(orgId),
        queryKeys.billing.purchases(orgId),
        queryKeys.billing.invoices(orgId),
      ])
        void client.invalidateQueries({ queryKey });
    }
  }, [data, orgId, client, onStatus, status.error, status.checkoutPending]);
  const denied = isBillingForbidden(status.error);
  const terminal =
    data?.fulfillment_status === "fulfilled" ||
    reviewRequired ||
    data?.payment_status === "failed" ||
    data?.payment_status === "expired";
  const needsCheck = status.isError || (status.exhausted && !terminal);
  const title =
    data?.fulfillment_status === "fulfilled"
      ? "Credits added"
      : reviewRequired
        ? "Payment needs review"
        : data?.payment_status === "paid"
          ? "Adding your credits"
          : data?.payment_status === "expired"
            ? "Checkout expired"
            : data?.payment_status === "failed"
              ? "Payment failed"
              : "Payment confirmation pending";
  const content = denied ? (
    <section aria-label="Purchase status" className="space-y-3">
      <h2 className="text-[17px] font-semibold">Purchase access denied</h2>
      <p role="alert">
        Your current account is not authorized to view this purchase. Contact an
        organization admin to restore access.
      </p>
    </section>
  ) : (
    <section aria-label="Purchase status" className="space-y-3">
      <h2 className="text-[17px] font-semibold">
        {data ? title : "Checking purchase"}
      </h2>
      {data && (
        <dl className="grid grid-cols-1 gap-3 text-[13px] sm:grid-cols-2">
          {data.amount_minor !== undefined && data.currency !== undefined && (
            <div>
              <dt className="text-muted-foreground">Amount</dt>
              <dd className="text-[22px] font-semibold break-all">
                {formatMinor(data.amount_minor, data.currency)}
              </dd>
            </div>
          )}
          {data.created_at !== undefined && (
            <div>
              <dt className="text-muted-foreground">Date (UTC)</dt>
              <dd>{new Date(data.created_at).toISOString().slice(0, 10)}</dd>
            </div>
          )}
          <div>
            <dt className="text-muted-foreground">Payment status</dt>
            <dd>{statusLabel(data.payment_status)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Credits status</dt>
            <dd>{statusLabel(data.fulfillment_status)}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-muted-foreground">Invoice</dt>
            <dd>
              {data.invoice_available === undefined
                ? "Checking invoice availability…"
                : data.invoice_available
                  ? "Available"
                  : data.fulfillment_status === "fulfilled"
                    ? "Invoice is being prepared."
                    : "Not available yet"}
            </dd>
          </div>
        </dl>
      )}
      <dl className="text-sm">
        <dt className="text-muted-foreground">Purchase reference</dt>
        <dd className="break-all font-mono text-xs">{purchaseId}</dd>
      </dl>
      {reviewRequired && (
        <p role="alert">
          Payment needs review. Contact support with this reference. Do not pay
          again for this purchase. {data?.review_message}
        </p>
      )}
      {data?.payment_status === "paid" &&
        data.fulfillment_status !== "fulfilled" &&
        !reviewRequired && (
          <p>
            Payment confirmed. Your credits are being added; you can safely
            return later.
          </p>
        )}
      {reviewRequired && (
        <a
          className="inline-block text-primary underline"
          href={`mailto:${appConfig.company.supportEmail}?subject=${encodeURIComponent("Credit purchase review")}&body=${encodeURIComponent(`Organization: ${orgId}\nPurchase reference: ${purchaseId}`)}`}
        >
          Contact support
        </a>
      )}
      {status.isError && (
        <p role="alert">
          Could not check this purchase. Check its status before making another
          payment.
        </p>
      )}
      {status.exhausted && !terminal && !status.isError && (
        <p>This purchase is taking longer than expected to complete.</p>
      )}
      {needsCheck && (
        <Button
          variant="outline"
          disabled={status.isFetching}
          onClick={() => void status.refetch()}
        >
          Check status
        </Button>
      )}
    </section>
  );
  // Keep the status controller mounted across dialog close/reopen so polling and recovery retain their lifetime.
  if (dialog)
    return (
      <Dialog
        open={dialog.open}
        onOpenChange={(open) => !open && dialog.onClose()}
      >
        <DialogContent
          className="max-h-[calc(100dvh-2rem)] overflow-y-auto"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            dialog.restoreFocus();
          }}
        >
          <DialogHeader>
            <DialogTitle>Purchase details</DialogTitle>
            <DialogDescription className="sr-only">
              Payment, credit fulfillment and invoice availability for this
              purchase.
            </DialogDescription>
          </DialogHeader>
          {content}
          {!denied && data?.invoice_available && (
            <Button variant="outline" onClick={dialog.onViewInvoices}>
              View invoices
            </Button>
          )}
        </DialogContent>
      </Dialog>
    );
  return (
    <div className="rounded-lg border bg-transparent p-4 text-[15px]">
      {content}
    </div>
  );
}
